import { Bar } from 'react-chartjs-2'
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend, TooltipItem } from 'chart.js'
import { StageLatency } from '../../api/models'
import PerformancePanel from './PerformancePanel'
import { STAGE_LABELS } from './performanceLabels'
import { formatSeconds } from './performanceFormat'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

type StageBreakdownChartProps = {
  stages: StageLatency[]
}

const hasAnyLatency = (stages: StageLatency[]): boolean =>
  stages.some((stage) => stage.p50_ms !== null || stage.p95_ms !== null)

const describeStages = (stages: StageLatency[]): string =>
  'Stage latency. ' +
  stages
    .map((stage) => `${STAGE_LABELS[stage.stage]}: p50 ${formatSeconds(stage.p50_ms)}, p95 ${formatSeconds(stage.p95_ms)}`)
    .join('; ')

const options = {
  indexAxis: 'y' as const,
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: 'top' as const },
    tooltip: {
      callbacks: {
        label: (item: TooltipItem<'bar'>) => `${item.dataset.label}: ${formatSeconds(item.parsed.x)}`,
      },
    },
  },
  scales: {
    x: {
      beginAtZero: true,
      ticks: { callback: (value: string | number) => formatSeconds(Number(value)) },
    },
  },
}

const StageBreakdownChart = ({ stages }: StageBreakdownChartProps) => {
  const chartData = {
    labels: stages.map((stage) => STAGE_LABELS[stage.stage]),
    datasets: [
      {
        label: 'p50',
        data: stages.map((stage) => stage.p50_ms),
        backgroundColor: '#8BACC4',
        borderRadius: 3,
      },
      {
        label: 'p95',
        data: stages.map((stage) => stage.p95_ms),
        backgroundColor: '#2B3A67',
        borderRadius: 3,
      },
    ],
  }

  return (
    <PerformancePanel title="Time per stage" isEmpty={!hasAnyLatency(stages)}
      emptyMessage="No stage timings recorded for this period."
    >
      <div style={{ height: 280 }}>
        <Bar data={chartData} options={options} role="img" aria-label={describeStages(stages)} />
      </div>
    </PerformancePanel>
  )
}

export default StageBreakdownChart
